import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Target, TrendingDown } from "lucide-react";

/**
 * "You keep missing…" — global weak-topic strip on the Dashboard.
 * Shows top 3 weakest concepts across the user's notes (last 30 days).
 * Hidden entirely if the user has no weak topics yet.
 */
export default function WeakTopicsStrip() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/study/weak-topics");
        if (!alive) return;
        setRows((data.weak_topics || []).slice(0, 3));
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <div
      data-testid="weak-topics-strip"
      className="mt-6 border-2 border-black bg-[#FEF9C3] p-4 shadow-[4px_4px_0_0_#0A0A0A]"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[#B91C1C]" />
        <p className="font-heading font-bold text-lg">You keep missing…</p>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 ml-1">
          Last 30 days
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {rows.map((r, i) => {
          const firstNote = r.note_titles?.[0];
          const inner = (
            <div className="border-2 border-black bg-white p-3 hover:bg-black hover:text-white transition-colors h-full">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-widest">
                  Weak concept
                </span>
                <span className="inline-flex items-center gap-1 font-mono text-[10px]">
                  <TrendingDown className="w-3 h-3" />
                  {Math.round(r.accuracy * 100)}%
                </span>
              </div>
              <p className="font-heading font-bold text-base line-clamp-2">{r.concept_tag}</p>
              {firstNote && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                  in {firstNote.title}
                </p>
              )}
            </div>
          );
          return (
            <li key={r.concept_tag + i} data-testid={`weak-strip-item-${i}`}>
              {firstNote ? (
                <Link to={`/app/note/${firstNote.id}`} className="block h-full">
                  {inner}
                </Link>
              ) : (
                inner
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
